import { OrderDirection } from './community-enums';

/**
 * The paging vocabulary every Community read shares, and the three rules that keep a query legal.
 *
 * Community deliberately answers 400 rather than clamping an over-cap `take`, because a paginator
 * advances `skip` by the size it asked for and a clamped page would leave rows nobody renders. The client
 * side of that bargain is this file: send what the caller meant, omit what they had no opinion about, and
 * never substitute a value silently.
 */

/** FeatureRequestLimits.DefaultTake, and the same number on both other paged reads. */
export const COMMUNITY_DEFAULT_TAKE = 20;

/** Above this the server answers 400. Passed explicitly to ServerPagedList so its own `?? 50` cannot apply. */
export const COMMUNITY_MAX_TAKE = 100;

/** Below this a query is a 400, so a shorter one is treated as no filter at all. */
export const COMMUNITY_MIN_QUERY_CHARACTERS = 2;

/** Every option is under COMMUNITY_MAX_TAKE, which is the only constraint the server puts on a paginator. */
export const COMMUNITY_PAGE_SIZE_OPTIONS: readonly number[] = [20, 50, 100];

/** What ServerPagedList's PagingState becomes once `refresh` is dropped: nothing in community/* reads it. */
export interface CommunityPageQuery {
  skip: number;
  take?: number;
  orderBy?: string;
  orderDirection?: OrderDirection;
}

type ParamValue = string | number | boolean;

/**
 * Drops keys whose value is absent, and only those.
 *
 * `0`, `false` and `''` all survive: `release=0` is a real release, `skip=0` is the first page and
 * `fulfilled=false` is a filter. A truthiness check here would drop all three. Absent keys have to
 * disappear rather than travel, because Angular serialises an explicit `undefined` as the string
 * "undefined", and none of the Community controllers carries [ApiController], so `take=undefined` binds
 * silently to null instead of failing — the bug would surface only as a wrong page size.
 */
export function communityParams(
  source: Readonly<Record<string, ParamValue | null | undefined>>,
): Record<string, ParamValue> {
  const params: Record<string, ParamValue> = {};
  for (const [key, value] of Object.entries(source)) {
    if (value != null) {
      params[key] = value;
    }
  }
  return params;
}

/**
 * A free-text filter, or nothing when it is too short to be legal.
 *
 * The house habit is `searchText.trim() || undefined`, which sends a one-character query and earns a 400
 * on the first keystroke. Treating anything shorter than the minimum as no filter is what the user meant.
 */
export function communityQuery(text: string | null | undefined): string | undefined {
  const trimmed = text?.trim() ?? '';
  return trimmed.length >= COMMUNITY_MIN_QUERY_CHARACTERS ? trimmed : undefined;
}

/**
 * Turns a Material column key into the sort member the server names.
 *
 * The server parses `orderBy` case-insensitively but by member name, so `createdAt` resolves and the
 * column key `created` does not. Falling back to the raw key would send the column name and earn a 400,
 * so an unknown key falls back to a member the caller nominates instead.
 */
export function communityOrderBy<TMap extends Record<string, string>>(
  map: TMap,
  key: string,
  fallback: keyof TMap & string,
): TMap[keyof TMap] {
  return (map[key] ?? map[fallback]) as TMap[keyof TMap];
}

/** 'asc' | 'desc' as Material reports it, including the third unsorted state, which sorts descending. */
export function communityOrderDirection(direction: string): OrderDirection {
  return direction === 'asc' ? OrderDirection.Ascending : OrderDirection.Descending;
}
